import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { flexGapPolyfill } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { ErrorMessage, ErrorMessageList } from 'shared/components/Message/ErrorMessage'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { Handler } from 'shared/helpers/typeHelper'
import { smoothScrollElementIntoView, YesNoRadioOptions } from 'shared/helpers/utils'
import { useDrivingAccidents } from 'shared/hooks/query/useDrivingAccidents'
import { useDrivingViolations } from 'shared/hooks/query/useDrivingViolations'
import { useHelpCenterUrl } from 'shared/hooks/useHelpCenterUrl'
import { useScrollToErrors } from 'shared/hooks/useScrollToErrors'
import { DrivingAccident, DrivingViolation } from 'shared/models/employment'
import { updateUserInfo, UpdateUserPayload, User } from 'shared/models/user'
import { sizes } from 'shared/theme'
import { errorD1 } from 'shared/theme/colors'
import { centeredContent } from 'shared/theme/mixins'
import { header4 } from 'shared/theme/typography'
import styled, { css } from 'styled-components'

import { YesNo } from '../../../models'
import { useUserProvider } from '../../Context/UserContext'
import { AccidentsList } from './AccidentsList'
import { AddAccident } from './AddAccident'
import { AddViolation } from './AddViolation'
import { DeleteAccidents } from './DeleteAccidents'
import { DeleteViolations } from './DeleteViolations'
import {
  DrivingHistoryForm,
  getCreateDrivingHistoryBody,
  getDrivingHistorySchema,
  getInitialForm,
} from './DrivingHistory.constants'
import { ViolationsList } from './ViolationsList'

interface DrivingHistoryProps {
  nextStep: Handler
  handleRedirect: Handler
  user: User
}

export const DrivingHistory = ({ nextStep, user, handleRedirect }: DrivingHistoryProps) => {
  const [loading, setLoading] = useState(false)
  const [accident, setAccident] = useState<DrivingAccident | {} | null>(null)
  const [violation, setViolation] = useState<DrivingViolation | {} | null>(null)
  const [deleteAllViolations, setDeleteAllViolations] = useState(false)
  const [deleteAllAccidents, setDeleteAllAccidents] = useState(false)
  const initialForm = getInitialForm(user)
  const { showBanner, clearUserCache } = useUserProvider()

  const {
    accidents,
    isLoading: isAccidentsLoading,
    refetch: refetchAccidents,
  } = useDrivingAccidents({ userId: user.id })

  const {
    violations,
    isLoading: isViolationsLoading,
    refetch: refetchViolations,
  } = useDrivingViolations({ userId: user.id })
  const hasAccidentMissingInfo = accidents?.some((a) => !a.isComplete)
  const hasViolationMissingInfo = violations?.some((v) => !v.isComplete)

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DrivingHistoryForm>({
    mode: 'onSubmit',
    defaultValues: initialForm,
    resolver: yupResolver(getDrivingHistorySchema(accidents, violations)),
  })
  const drivingHelpUrl = useHelpCenterUrl('driving-with-cloudtrucks')
  const hadDuiCriminalCharges = watch('had_dui_criminal_charges')
  const hadAtFaultIncident = watch('had_at_fault_incident')
  const hadMajorViolation = watch('had_major_violation')
  const hadPrivilegeDenied = watch('had_privilege_denied')
  const hadPrivilegeSuspended = watch('had_privilege_suspended')

  useEffect(() => {
    if (violations.length || user.driver?.hadMajorViolation) {
      setValue('had_major_violation', YesNo.YES)
    }
    if (accidents.length || user.driver?.hadAtFaultIncident) {
      setValue('had_at_fault_incident', YesNo.YES)
    }
  }, [
    accidents.length,
    violations.length,
    user.driver?.hadMajorViolation,
    user.driver?.hadAtFaultIncident,
    setValue,
  ])

  useEffect(() => {
    if (hadMajorViolation === YesNo.NO && violations.length) {
      setDeleteAllViolations(true)
    }
    if (hadAtFaultIncident === YesNo.NO && accidents.length) {
      setDeleteAllAccidents(true)
    }
  }, [hadMajorViolation, hadAtFaultIncident, violations.length, accidents.length])

  useScrollToErrors(errors)

  const updateDriverMutation = useMutation(
    (payload: UpdateUserPayload) => updateUserInfo(user.id, payload, { vgs: true, v3Api: true }),
    {
      onSuccess: () => {
        clearUserCache()
        setLoading(false)
        nextStep()
      },
      onError: (err: any) => {
        showBanner('error', err)
        setLoading(false)
      },
    }
  )

  function onDeleteViolationsClose() {
    setValue('had_major_violation', YesNo.YES)
    setDeleteAllViolations(false)
  }

  function onDeleteAccidentsClose() {
    setValue('had_at_fault_incident', YesNo.YES)
    setDeleteAllAccidents(false)
  }

  const onSubmit = async (formData: DrivingHistoryForm) => {
    if (hasAccidentMissingInfo || hasViolationMissingInfo) return
    setLoading(true)
    const driverPayload = getCreateDrivingHistoryBody(formData as Required<DrivingHistoryForm>)
    await updateDriverMutation.mutateAsync({
      driver: driverPayload,
      had_privilege_denied_detail:
        formData.had_privilege_denied === YesNo.YES ? formData.had_privilege_denied_detail : undefined,
      had_privilege_suspended_detail:
        formData.had_privilege_suspended === YesNo.YES ? formData.had_privilege_suspended_detail : undefined,
    })
  }

  if (isAccidentsLoading || isViolationsLoading) {
    return <InlineSpinner />
  }

  async function handleViolationSuccess() {
    setValue('had_major_violation', YesNo.YES)
    await refetchViolations()
    handleViolationClose()
  }

  async function handleAllViolationsDeleteSuccess() {
    await refetchViolations()
    setDeleteAllViolations(false)
    setTimeout(() => smoothScrollElementIntoView(document.getElementById('had-major-violation'), 10), 100)
  }

  async function handleViolationClose() {
    setViolation(null)
    setTimeout(() => smoothScrollElementIntoView(document.getElementById('had-major-violation'), 10), 100)
  }

  async function handleAccidentSuccess() {
    setValue('had_at_fault_incident', YesNo.YES)
    await refetchAccidents()
    handleAccidentClose()
  }

  async function handleAllAccidentsDeleteSuccess() {
    await refetchAccidents()
    setDeleteAllAccidents(false)
    setTimeout(() => smoothScrollElementIntoView(document.getElementById('had-major-violation'), 10), 100)
  }

  async function handleAccidentClose() {
    setAccident(null)
    setTimeout(() => smoothScrollElementIntoView(document.getElementById('had-at-fault-incident'), 10), 100)
  }

  function renderErrorMessage() {
    if (!hasAccidentMissingInfo && !hasViolationMissingInfo) return null

    return (
      <>
        <StyledErrorMessage>
          <Text variant="header4">Please complete the missing information:</Text>
          <ErrorMessageList>
            {hasViolationMissingInfo && (
              <li>
                <Text variant="paragraph2">Missing violations infomation</Text>
              </li>
            )}
            {hasAccidentMissingInfo && (
              <li>
                <Text variant="paragraph2">Missing accidents infomation</Text>
              </li>
            )}
          </ErrorMessageList>
        </StyledErrorMessage>
        <Box mt={5} />
      </>
    )
  }

  return (
    <>
      {accident && (
        <AddAccident
          accident={accident}
          onClose={handleAccidentClose}
          onSuccess={handleAccidentSuccess}
          user={user}
        />
      )}
      {violation && (
        <AddViolation
          violation={violation}
          onClose={handleViolationClose}
          onSuccess={handleViolationSuccess}
          user={user}
        />
      )}
      <div style={{ display: violation || accident ? 'none' : 'block' }}>
        <Text variant="header2" align="center">
          Add your driving history
        </Text>
        <Box mt={7} />

        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          {renderErrorMessage()}
          <Subtitle $marginTop="-12px">Years of experience operating the following trailer types</Subtitle>
          <CenteredWrap>
            <Controller
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Dry van"
                  type="number"
                  error={!!fieldState.error}
                  noMargin
                  min="0"
                />
              )}
              name="years_dry_van"
              control={control}
            />
            <Controller
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Flatbed"
                  type="number"
                  error={!!fieldState.error}
                  noMargin
                  min="0"
                />
              )}
              name="years_flatbed"
              control={control}
            />
            <Controller
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label="Reefer"
                  type="number"
                  error={!!fieldState.error}
                  noMargin
                  min="0"
                />
              )}
              name="years_reefer"
              control={control}
            />
          </CenteredWrap>
          {errors.years_dry_van || errors.years_flatbed || errors.years_reefer ? (
            <Text variant="paragraph3" fontColor={errorD1} mt={1} align="left" styles="width: 100%;">
              Enter years of experience for each trailer type
            </Text>
          ) : null}

          <Box mt={5} />

          <div id="had_dui_criminal_charges"></div>
          <Controller
            render={({ field, fieldState }) => (
              <RadioButtonGroup
                {...field}
                options={YesNoRadioOptions}
                row
                error={fieldState.error?.message}
                width="100%"
                noMargin
                label="Have you had a DUI, drug possession or influence charge, or any other criminal charges in the past 5 years?"
              />
            )}
            name="had_dui_criminal_charges"
            control={control}
          />

          {(errors.had_dui_criminal_charges?.type === 'not_eligible' ||
            hadDuiCriminalCharges === YesNo.YES) && (
            <ErrorMessage>
              <Text variant="header3">You don’t meet our driver eligibility requirements</Text>
              <Text variant="paragraph1" mb={0.5}>
                We’ll reach out if our eligibility criteria changes. If you have any questions, please contact
                hello@cloudtrucks.com.
              </Text>
              <ExternalLink href={drivingHelpUrl} target="_blank" variant="header3">
                Learn more about our requirements
              </ExternalLink>
            </ErrorMessage>
          )}
          <Box mt={4} />

          <div id="had-at-fault-incident"></div>
          <Controller
            render={({ field, fieldState }) => (
              <RadioButtonGroup
                {...field}
                row
                label="Have you had any accidents in the past 5 years?"
                options={YesNoRadioOptions}
                error={fieldState.error?.message}
                width="100%"
                noMargin
              />
            )}
            name="had_at_fault_incident"
            control={control}
          />
          {hadAtFaultIncident === YesNo.YES && (
            <>
              <Box mt={errors.had_at_fault_incident ? 2 : 1} />
              <AccidentsList
                accidents={accidents}
                onAdd={() => setAccident({})}
                onEdit={(violation) => setAccident(violation)}
              />
            </>
          )}
          <Box mt={4} />

          <div id="had-major-violation"></div>
          <Controller
            render={({ field, fieldState }) => (
              <RadioButtonGroup
                {...field}
                row
                label="Have you had any moving violations in the past 5 years?"
                options={YesNoRadioOptions}
                error={fieldState.error?.message}
                width="100%"
                noMargin
              />
            )}
            name="had_major_violation"
            control={control}
          />
          {hadMajorViolation === YesNo.YES && (
            <>
              <Box mt={errors.had_major_violation ? 2 : 1} />
              <ViolationsList
                violations={violations}
                onAdd={() => setViolation({})}
                onEdit={(violation) => setViolation(violation)}
              />
            </>
          )}
          <Box mt={4} />

          <Controller
            render={({ field, fieldState }) => (
              <RadioButtonGroup
                {...field}
                row
                label="Have you ever had a license, permit, or privilege suspended or revoked?"
                options={YesNoRadioOptions}
                error={fieldState.error?.message}
                width="100%"
                noMargin
              />
            )}
            name="had_privilege_suspended"
            control={control}
          />
          {hadPrivilegeSuspended === YesNo.YES && (
            <>
              <Box mt={1} />
              <Controller
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    placeholder="Add a short explanation"
                    error={fieldState.error?.message}
                    noMargin
                  />
                )}
                name="had_privilege_suspended_detail"
                control={control}
              />
            </>
          )}
          <Box mt={4} />
          <Controller
            render={({ field, fieldState }) => (
              <RadioButtonGroup
                {...field}
                row
                label="Have you ever been denied a license, permit, or privilege to operate a motor vehicle?"
                options={YesNoRadioOptions}
                error={fieldState.error?.message}
                width="100%"
                noMargin
              />
            )}
            name="had_privilege_denied"
            control={control}
          />
          {hadPrivilegeDenied === YesNo.YES && (
            <>
              <Box mt={1} />
              <Controller
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    placeholder="Add a short explanation"
                    error={fieldState.error?.message}
                    noMargin
                  />
                )}
                name="had_privilege_denied_detail"
                control={control}
              />
            </>
          )}
          <Box mt={8} />
          <Button type="submit" size={sizes.extraLarge} margin="16px 0" isLoading={loading}>
            Continue
          </Button>
          <Box mt={2} />
          <Button variant="text" onClick={handleRedirect}>
            Back to Tasklist
          </Button>
          <Box mt={12} />

          {deleteAllViolations && (
            <DeleteViolations
              onSuccess={handleAllViolationsDeleteSuccess}
              onClose={onDeleteViolationsClose}
              violations={violations}
              user={user}
            />
          )}
          {deleteAllAccidents && (
            <DeleteAccidents
              onSuccess={handleAllAccidentsDeleteSuccess}
              onClose={onDeleteAccidentsClose}
              accidents={accidents}
              user={user}
            />
          )}
        </FormContainer>
      </div>
    </>
  )
}

const StyledErrorMessage = styled(ErrorMessage)`
  width: 100%;
`

const FormContainer = styled.form`
  ${centeredContent};
  flex-direction: column;
  max-width: 424px;
  margin: 0 auto;

  @media (max-width: 460px) {
    width: 100%;
  }
`

const CenteredWrap = styled.div`
  ${centeredContent};
  width: 100%;
  ${flexGapPolyfill('10px')};

  @media (max-width: 460px) {
    flex-direction: column;
    margin-bottom: 20px;

    > div {
      width: 100%;
      margin: 0;
    }
  }
`

const Subtitle = styled.h4<{ $marginTop?: string }>`
  ${header4};
  width: 100%;
  margin: 0 0 16px 0;
  ${(p) =>
    p.$marginTop &&
    css`
      margin-top: ${p.$marginTop};
    `};
`
